/**
 * Frontend behaviour for the newhome blocks.
 *
 * - Scroll-reveal: adds `.is-visible` to each newhome <section> when it enters
 *   the viewport (mirrors the IntersectionObserver from page-newhome.php).
 * - Hero parallax: smooth rAF-driven translate on the hero video.
 *
 * Works independently per block because each block self-wraps in
 * `<div class="newhome nh-anim">…<section class="nh-…">…</section></div>`.
 *
 * @package ow-features
 */

( function () {
	'use strict';

	var reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	function setupReveal() {
		var sections = document.querySelectorAll( '.newhome.nh-anim > section' );
		if ( ! sections.length ) {
			return;
		}

		if ( ! ( 'IntersectionObserver' in window ) || reducedMotion ) {
			sections.forEach( function ( el ) {
				el.classList.add( 'is-visible' );
			} );
			return;
		}

		var io = new IntersectionObserver(
			function ( entries ) {
				entries.forEach( function ( entry ) {
					if ( entry.isIntersecting ) {
						entry.target.classList.add( 'is-visible' );
						io.unobserve( entry.target );
					}
				} );
			},
			{ threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
		);

		sections.forEach( function ( el ) {
			io.observe( el );
		} );
	}

	function setupParallaxLayers( options ) {
		var hosts  = document.querySelectorAll( options.hostSelector );
		if ( ! hosts.length ) {
			return;
		}

		if ( reducedMotion ) {
			hosts.forEach( function ( host ) {
				if ( options.readyClass && host.querySelector( options.layerSelector ) ) {
					host.classList.remove( options.preparingClass );
					host.classList.add( options.readyClass );
				}
			} );
			return;
		}

		var layers = [];
		var revealStartedAt = window.performance && performance.now ? performance.now() : Date.now();
		hosts.forEach( function ( host ) {
			var el = host.querySelector( options.layerSelector );
			if ( el ) {
				if ( options.preparingClass ) {
					host.classList.remove( options.readyClass );
					host.classList.add( options.preparingClass );
				}
				layers.push( {
					host: host,
					el: el,
					range: options.range,
					baseScale: options.baseScale,
					cssVariable: options.cssVariable || '',
					ease: 'number' === typeof options.ease ? options.ease : 0.08,
					clampToCover: true === options.clampToCover,
					safety: 'number' === typeof options.safety ? options.safety : 4,
					current: 0,
					target: 0,
					inView: true,
					mediaReady: false,
					transformReady: false,
					syncReady: ! options.syncSelector,
					revealed: false,
					revealTimer: null,
					revealDelay: 'number' === typeof options.revealDelay ? options.revealDelay : 0,
					mediaReadyTimeout: 'number' === typeof options.mediaReadyTimeout ? options.mediaReadyTimeout : 3000,
					syncSelector: options.syncSelector || '',
					preparingClass: options.preparingClass || '',
					readyClass: options.readyClass || '',
				} );
			}
		} );
		if ( ! layers.length ) {
			return;
		}

		var EPS  = 0.05;
		var rafId = null;

		function revealLayer( layer ) {
			if ( layer.revealed || ! layer.readyClass || ! layer.mediaReady || ! layer.transformReady || ! layer.syncReady ) {
				return;
			}

			if ( layer.revealDelay > 0 ) {
				var now = window.performance && performance.now ? performance.now() : Date.now();
				var remaining = Math.max( 0, layer.revealDelay - ( now - revealStartedAt ) );

				if ( remaining > 0 ) {
					if ( layer.revealTimer === null ) {
						layer.revealTimer = window.setTimeout( function () {
							layer.revealTimer = null;
							revealLayer( layer );
						}, remaining );
					}
					return;
				}
			}

			layer.revealed = true;
			layer.host.classList.remove( layer.preparingClass );
			// Force the browser to commit the hidden state before starting the fade-in.
			void layer.host.offsetWidth;
			layer.host.classList.add( layer.readyClass );
		}

		function markMediaReady( layer ) {
			layer.mediaReady = true;
			revealLayer( layer );
		}

		function watchMediaReady( layer ) {
			var media = layer.el;
			var completed = false;
			var done = function () {
				if ( completed ) {
					return;
				}
				completed = true;
				markMediaReady( layer );
			};
			var decodeThenDone = function () {
				if ( media.decode && media.naturalWidth ) {
					media.decode().then( done ).catch( done );
					return;
				}
				done();
			};

			if ( 'IMG' === media.tagName ) {
				if ( media.complete && media.naturalWidth ) {
					decodeThenDone();
				} else {
					media.addEventListener( 'load', decodeThenDone, { once: true } );
					media.addEventListener( 'error', done, { once: true } );
					window.setTimeout( function () {
						if ( media.complete && media.naturalWidth ) {
							decodeThenDone();
						}
					}, 5000 );
				}
			} else if ( 'VIDEO' === media.tagName ) {
				if ( media.readyState >= 2 ) {
					done();
				} else {
					media.addEventListener( 'loadeddata', done, { once: true } );
					media.addEventListener( 'error', done, { once: true } );
				}
			} else {
				done();
			}

			if ( 'IMG' !== media.tagName && layer.mediaReadyTimeout > 0 ) {
				window.setTimeout( done, layer.mediaReadyTimeout );
			}
		}

		function watchSyncReady( layer ) {
			if ( layer.syncReady || ! layer.syncSelector ) {
				return;
			}

			var scope = layer.host.closest( '.newhome' ) || document;
			var syncElement = scope.querySelector( layer.syncSelector ) || document.querySelector( layer.syncSelector );
			if ( ! syncElement ) {
				layer.syncReady = true;
				revealLayer( layer );
				return;
			}

			var completed = false;
			var done = function () {
				if ( completed ) {
					return;
				}
				completed = true;
				layer.syncReady = true;
				revealLayer( layer );
			};
			var checkVisualStart = function () {
				if ( completed ) {
					return;
				}
				var styles = window.getComputedStyle( syncElement );
				if ( 'none' === styles.animationName || parseFloat( styles.opacity ) > 0 ) {
					done();
					return;
				}
				window.setTimeout( checkVisualStart, 50 );
			};

			syncElement.addEventListener( 'animationstart', done, { once: true } );
			checkVisualStart();
		}

		function getSafeRange( layer ) {
			if ( ! layer.clampToCover ) {
				return layer.range;
			}

			var hostHeight = layer.host.clientHeight || layer.host.getBoundingClientRect().height;
			var layerHeight = layer.el.offsetHeight || hostHeight;
			var scale = layer.baseScale || 1;
			var safeRange = ( ( layerHeight * scale ) - hostHeight ) / 2 - layer.safety;

			return Math.max( 0, Math.min( layer.range, safeRange ) );
		}

		function readTargets() {
			var winH = window.innerHeight || document.documentElement.clientHeight;
			layers.forEach( function ( layer ) {
				if ( ! layer.inView ) {
					return;
				}
				var rect = layer.host.getBoundingClientRect();
				if ( rect.bottom < -200 || rect.top > winH + 200 ) {
					return;
				}
				var progress = ( ( winH / 2 ) - ( rect.top + rect.height / 2 ) ) / winH;
				var safeRange = getSafeRange( layer );
				layer.target = Math.max( -safeRange, Math.min( safeRange, progress * safeRange * 2 ) );
			} );
		}

		function tick() {
			var stillMoving = false;
			layers.forEach( function ( layer ) {
				var diff = layer.target - layer.current;
				if ( layer.ease >= 1 ) {
					layer.current = layer.target;
				} else if ( Math.abs( diff ) > EPS ) {
					layer.current += diff * layer.ease;
					stillMoving = true;
				} else if ( layer.current !== layer.target ) {
					layer.current = layer.target;
				}
				if ( layer.cssVariable ) {
					layer.el.style.setProperty( layer.cssVariable, layer.current.toFixed( 2 ) + 'px' );
				} else {
					var scalePart = layer.baseScale ? ' scale(' + layer.baseScale + ')' : '';
					layer.el.style.transform = 'translate3d(0, ' + layer.current.toFixed( 2 ) + 'px, 0)' + scalePart;
				}
				layer.transformReady = true;
				revealLayer( layer );
			} );
			rafId = stillMoving ? requestAnimationFrame( tick ) : null;
		}

		function start() {
			if ( rafId === null ) {
				rafId = requestAnimationFrame( tick );
			}
		}

		function onScroll() {
			readTargets();
			start();
		}

		if ( 'IntersectionObserver' in window ) {
			layers.forEach( function ( layer ) {
				var io = new IntersectionObserver(
					function ( entries ) {
						layer.inView = entries[ 0 ].isIntersecting;
						if ( layer.inView ) {
							readTargets();
							start();
						}
					},
					{ rootMargin: '300px 0px' }
				);
				io.observe( layer.host );
			} );
		}

		window.addEventListener( 'scroll', onScroll, { passive: true } );
		window.addEventListener( 'resize', onScroll, { passive: true } );
		layers.forEach( watchMediaReady );
		layers.forEach( watchSyncReady );
		readTargets();
		start();
	}

	function setupHeroParallax() {
		var videoParallaxEnabled = window.matchMedia( '(min-width: 768px)' ).matches;

		setupParallaxLayers( {
			hostSelector: '.nh-hero__media',
			layerSelector: '.nh-hero__image',
			range: 28,
			baseScale: 1.2,
			ease: 1,
			clampToCover: true,
			safety: 8,
			revealDelay: 1600,
			preparingClass: 'nh-parallax-preparing',
			readyClass: 'nh-parallax-ready',
		} );

		setupParallaxLayers( {
			hostSelector: '.newhome:not(.nh-hero--split) .nh-hero__media--video',
			layerSelector: '.nh-hero__video',
			// Keep the synchronized video reveal on every viewport, but only
			// move the hero video on desktop.
			range: videoParallaxEnabled ? 28 : 0,
			baseScale: 1.2,
			ease: 1,
			clampToCover: true,
			safety: 8,
			mediaReadyTimeout: 0,
			syncSelector: '.nh-hero__subtitle',
			preparingClass: 'nh-video-parallax-preparing',
			readyClass: 'nh-video-parallax-ready',
		} );
	}

	function setupEngagementCardParallax() {
		setupParallaxLayers( {
			hostSelector: '.nh-engagement__card-media[data-nh-card-parallax]',
			layerSelector: '.nh-engagement__card-image',
			range: 14,
			baseScale: 1.12,
		} );
	}

	function setupHeroContactScroll() {
		var triggers = document.querySelectorAll( '.nh-hero__cta[data-nh-scroll-target], .nh-cta__btn[data-nh-scroll-target], .nc-btn[data-nh-scroll-target], .header-nav__button-link[data-nh-scroll-target]' );
		if ( ! triggers.length ) {
			return;
		}

		triggers.forEach( function ( trigger ) {
			trigger.addEventListener( 'click', function ( e ) {
				var selector = trigger.getAttribute( 'data-nh-scroll-target' ) || '.wp-block-ow-feature-block-contact-form';
				var scope    = trigger.closest( '.newhome' ) || document;
				var target   = scope.querySelector( selector ) || document.querySelector( selector );

				if ( ! target ) {
					return;
				}

				e.preventDefault();

				var adminBar = document.getElementById( 'wpadminbar' );
				var header   = document.querySelector( '.site-header, .header-container' );
				var offset   = adminBar ? adminBar.getBoundingClientRect().height : 0;
				offset      += header ? header.getBoundingClientRect().height : 0;
				var top      = target.getBoundingClientRect().top + window.pageYOffset - offset - 24;

				window.scrollTo( {
					top: Math.max( 0, top ),
					behavior: reducedMotion ? 'auto' : 'smooth',
				} );
			} );
		} );
	}

	function setupCapabilitiesTabs() {
		var blocks = document.querySelectorAll( '.nh-capabilities' );
		if ( ! blocks.length ) {
			return;
		}

		blocks.forEach( function ( block ) {
			var tabs   = Array.prototype.slice.call( block.querySelectorAll( '[role="tab"]' ) );
			var panels = Array.prototype.slice.call( block.querySelectorAll( '[role="tabpanel"]' ) );
			if ( ! tabs.length || ! panels.length ) {
				return;
			}

			function loadVideo( video ) {
				if ( video && ! video.src && video.dataset.src ) {
					video.src = video.dataset.src;
					video.removeAttribute( 'data-src' );
					video.load();
				}
			}

			function activate( idx, focusTab, animate ) {
				tabs.forEach( function ( tab, i ) {
					var active = i === idx;
					tab.setAttribute( 'aria-selected', active ? 'true' : 'false' );
					tab.tabIndex = active ? 0 : -1;
				} );
				panels.forEach( function ( panel, i ) {
					var active = i === idx;
					panel.hidden = ! active;
					panel.classList.remove( 'is-entering' );
					var video = panel.querySelector( 'video' );
					if ( video ) {
						if ( active ) {
							loadVideo( video );
							try { video.currentTime = 0; } catch ( e ) {}
							var p = video.play();
							if ( p && p.catch ) { p.catch( function () {} ); }
						} else {
							video.pause();
						}
					}
					if ( active && animate ) {
						// Force reflow so the animation re-triggers on every tab change.
						void panel.offsetWidth;
						panel.classList.add( 'is-entering' );
					}
				} );
				if ( focusTab ) {
					tabs[ idx ].focus();
				}
			}

			tabs.forEach( function ( tab, i ) {
				tab.addEventListener( 'click', function () { activate( i, false, true ); } );
				tab.addEventListener( 'keydown', function ( e ) {
					if ( e.key === 'ArrowLeft' || e.key === 'ArrowRight' ) {
						e.preventDefault();
						var dir  = e.key === 'ArrowRight' ? 1 : -1;
						var next = ( i + dir + tabs.length ) % tabs.length;
						activate( next, true, true );
					} else if ( e.key === 'Home' ) {
						e.preventDefault();
						activate( 0, true, true );
					} else if ( e.key === 'End' ) {
						e.preventDefault();
						activate( tabs.length - 1, true, true );
					}
				} );
			} );

			// Pause any non-default panel's video on init (defensive — server already hides them).
			panels.forEach( function ( panel ) {
				if ( panel.hidden ) {
					var v = panel.querySelector( 'video' );
					if ( v ) { v.pause(); }
				}
			} );

			function loadActivePanelMedia() {
				var activePanel = panels.find( function ( panel ) { return ! panel.hidden; } );
				var activeVideo = activePanel ? activePanel.querySelector( 'video' ) : null;
				if ( activeVideo ) {
					loadVideo( activeVideo );
					var playback = activeVideo.play();
					if ( playback && playback.catch ) { playback.catch( function () {} ); }
				}
			}

			if ( 'IntersectionObserver' in window ) {
				var mediaObserver = new IntersectionObserver( function ( entries ) {
					if ( entries[ 0 ].isIntersecting ) {
						loadActivePanelMedia();
						mediaObserver.disconnect();
					}
				}, { rootMargin: '300px 0px' } );
				mediaObserver.observe( block );
			} else {
				loadActivePanelMedia();
			}
		} );
	}

	/**
	 * Keep decorative autoplay video from consuming CPU and bandwidth while it
	 * is outside the viewport or the page is in a background tab.
	 */
	function setupViewportVideoPlayback() {
		var videos = document.querySelectorAll( '.newhome video[autoplay], .nh-capabilities video[autoplay]' );
		if ( ! videos.length ) {
			return;
		}

		function setPlayback( video, shouldPlay ) {
			if ( shouldPlay && ! reducedMotion && ! document.hidden ) {
				var playback = video.play();
				if ( playback && playback.catch ) {
					playback.catch( function () {} );
				}
				return;
			}
			video.pause();
		}

		if ( ! ( 'IntersectionObserver' in window ) ) {
			videos.forEach( function ( video ) {
				setPlayback( video, ! reducedMotion );
			} );
			return;
		}

		var visibleVideos = new WeakSet();
		var observer = new IntersectionObserver( function ( entries ) {
			entries.forEach( function ( entry ) {
				if ( entry.isIntersecting ) {
					visibleVideos.add( entry.target );
				} else {
					visibleVideos.delete( entry.target );
				}
				setPlayback( entry.target, entry.isIntersecting );
			} );
		}, { threshold: 0.05 } );

		videos.forEach( function ( video ) {
			observer.observe( video );
		} );

		document.addEventListener( 'visibilitychange', function () {
			videos.forEach( function ( video ) {
				setPlayback( video, visibleVideos.has( video ) );
			} );
		} );
	}

	function boot() {
		setupReveal();
		setupHeroParallax();
		setupEngagementCardParallax();
		setupHeroContactScroll();
		setupCapabilitiesTabs();
		setupViewportVideoPlayback();
	}

	if ( document.readyState === 'loading' ) {
		document.addEventListener( 'DOMContentLoaded', boot );
	} else {
		boot();
	}
} )();
